/**
 * Report period helpers: rolling day windows and natural calendar months.
 * Always normalize to inclusive YYYY-MM-DD for API consumers.
 */

export type PeriodMode = 'day' | 'month';

export type DayPresetId = 'last_7d' | 'last_30d' | 'last_90d' | 'last_365d' | 'custom';

export type MonthPresetId =
  | 'this_month'
  | 'last_month'
  | 'last_3_months'
  | 'last_6_months'
  | 'last_12_months'
  | 'custom';

export type MonthKind = 'single' | 'range';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface ReportPeriodBase extends DateRange {
  mode: PeriodMode;
  label?: string;
}

export interface DayPeriod extends ReportPeriodBase {
  mode: 'day';
  presetId: DayPresetId;
}

export interface MonthSinglePeriod extends ReportPeriodBase {
  mode: 'month';
  kind: 'single';
  month: string; // YYYY-MM
  presetId: MonthPresetId;
}

export interface MonthRangePeriod extends ReportPeriodBase {
  mode: 'month';
  kind: 'range';
  startMonth: string; // YYYY-MM
  endMonth: string; // YYYY-MM
  presetId: MonthPresetId;
}

export type ReportPeriod = DayPeriod | MonthSinglePeriod | MonthRangePeriod;

export function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatMonthKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function parseMonthKey(monthKey: string): { year: number; monthIndex: number } {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey);
  if (!match) {
    throw new RangeError(`Invalid month key: ${monthKey}`);
  }
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) {
    throw new RangeError(`Invalid month key: ${monthKey}`);
  }
  return { year, monthIndex };
}

export function shiftMonth(monthKey: string, delta: number): string {
  if (!Number.isInteger(delta)) {
    throw new RangeError('delta must be an integer');
  }
  const { year, monthIndex } = parseMonthKey(monthKey);
  const shifted = new Date(year, monthIndex + delta, 1);
  return formatMonthKey(shifted);
}

/** Last calendar day of the given month (local). */
export function lastDayOfMonth(monthKey: string): Date {
  const { year, monthIndex } = parseMonthKey(monthKey);
  return new Date(year, monthIndex + 1, 0);
}

/**
 * Inclusive natural-month range.
 * Current month ends at `today` (in-progress); past months end on the last day.
 */
export function monthToDateRange(monthKey: string, today = new Date()): DateRange {
  const { year, monthIndex } = parseMonthKey(monthKey);
  const start = new Date(year, monthIndex, 1);
  const todayKey = formatMonthKey(today);
  const end =
    monthKey === todayKey
      ? new Date(today.getFullYear(), today.getMonth(), today.getDate())
      : lastDayOfMonth(monthKey);

  if (formatDateInputValue(end) < formatDateInputValue(start)) {
    throw new RangeError('end before start for month range');
  }

  return {
    startDate: formatDateInputValue(start),
    endDate: formatDateInputValue(end),
  };
}

/**
 * Inclusive multi-month window ending at `endMonth` (default: current month).
 * `monthCount` includes the end month (e.g. 3 → end and two prior months).
 */
export function getRelativeMonthRange(
  monthCount: number,
  endMonth?: string,
  today = new Date(),
): { startMonth: string; endMonth: string } & DateRange {
  if (!Number.isInteger(monthCount) || monthCount < 1) {
    throw new RangeError('monthCount must be a positive integer');
  }
  const resolvedEnd = endMonth ?? formatMonthKey(today);
  const startMonth = shiftMonth(resolvedEnd, -(monthCount - 1));
  const start = monthToDateRange(startMonth, today).startDate;
  const end = monthToDateRange(resolvedEnd, today).endDate;
  return { startMonth, endMonth: resolvedEnd, startDate: start, endDate: end };
}

export function getRelativeDateRange(dayCount: number, end = new Date()): DateRange {
  if (!Number.isInteger(dayCount) || dayCount < 1) {
    throw new RangeError('dayCount must be a positive integer');
  }

  const start = new Date(end);
  start.setDate(end.getDate() - (dayCount - 1));
  return {
    startDate: formatDateInputValue(start),
    endDate: formatDateInputValue(end),
  };
}

const DAY_PRESET_DAYS: Record<Exclude<DayPresetId, 'custom'>, number> = {
  last_7d: 7,
  last_30d: 30,
  last_90d: 90,
  last_365d: 365,
};

export function resolveDayPreset(presetId: Exclude<DayPresetId, 'custom'>, today = new Date()): DayPeriod {
  const range = getRelativeDateRange(DAY_PRESET_DAYS[presetId], today);
  return {
    mode: 'day',
    presetId,
    ...range,
  };
}

export function resolveMonthSingle(monthKey: string, today = new Date()): MonthSinglePeriod {
  const current = formatMonthKey(today);
  const range = monthToDateRange(monthKey, today);
  let presetId: MonthPresetId = 'custom';
  if (monthKey === current) {
    presetId = 'this_month';
  } else if (monthKey === shiftMonth(current, -1)) {
    presetId = 'last_month';
  }
  return {
    mode: 'month',
    kind: 'single',
    month: monthKey,
    presetId,
    ...range,
  };
}

export function resolveMonthPreset(
  presetId: Exclude<MonthPresetId, 'custom'>,
  today = new Date(),
): MonthSinglePeriod | MonthRangePeriod {
  const current = formatMonthKey(today);
  if (presetId === 'this_month') {
    return resolveMonthSingle(current, today);
  }
  if (presetId === 'last_month') {
    return resolveMonthSingle(shiftMonth(current, -1), today);
  }
  const count =
    presetId === 'last_3_months' ? 3 : presetId === 'last_6_months' ? 6 : 12;
  const range = getRelativeMonthRange(count, current, today);
  return {
    mode: 'month',
    kind: 'range',
    presetId,
    startMonth: range.startMonth,
    endMonth: range.endMonth,
    startDate: range.startDate,
    endDate: range.endDate,
  };
}

/** Month keys from `fromMonth` to `toMonth` inclusive, newest first. */
export function listMonthOptions(fromMonth: string, toMonth: string): string[] {
  parseMonthKey(fromMonth);
  parseMonthKey(toMonth);
  if (fromMonth > toMonth) {
    throw new RangeError('fromMonth must be <= toMonth');
  }
  const options: string[] = [];
  let cursor = toMonth;
  while (cursor >= fromMonth) {
    options.push(cursor);
    if (cursor === fromMonth) break;
    cursor = shiftMonth(cursor, -1);
  }
  return options;
}

/** Default dropdown window: last `monthsBack` months including current (default 36). */
export function defaultMonthOptions(today = new Date(), monthsBack = 36): string[] {
  if (!Number.isInteger(monthsBack) || monthsBack < 1) {
    throw new RangeError('monthsBack must be a positive integer');
  }
  const toMonth = formatMonthKey(today);
  const fromMonth = shiftMonth(toMonth, -(monthsBack - 1));
  return listMonthOptions(fromMonth, toMonth);
}

export function canShiftMonthForward(monthKey: string, today = new Date()): boolean {
  return monthKey < formatMonthKey(today);
}

export function matchDayPreset(
  startDate: string,
  endDate: string,
  today = new Date(),
): DayPresetId {
  const todayStr = formatDateInputValue(today);
  if (endDate !== todayStr) return 'custom';
  for (const presetId of ['last_7d', 'last_30d', 'last_90d', 'last_365d'] as const) {
    const expected = getRelativeDateRange(DAY_PRESET_DAYS[presetId], today);
    if (startDate === expected.startDate && endDate === expected.endDate) {
      return presetId;
    }
  }
  return 'custom';
}

export function createDefaultPeriod(
  options: { mode?: PeriodMode; dayPreset?: Exclude<DayPresetId, 'custom'>; monthPreset?: Exclude<MonthPresetId, 'custom'> } = {},
  today = new Date(),
): ReportPeriod {
  const mode = options.mode ?? 'day';
  if (mode === 'month') {
    return resolveMonthPreset(options.monthPreset ?? 'this_month', today);
  }
  return resolveDayPreset(options.dayPreset ?? 'last_30d', today);
}
